import React, { useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Pencil,
  Trash2,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Eye,
} from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Card, CardTitle, CardFooter } from "../../../../components/ui/Card";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { FieldLabel } from "../../../../components/ui/FieldLabel";

// ─── Base de conocimiento del asistente IA ──────────────────────────────────
//
// Editor estructurado de la base de conocimiento que se inyecta en el system
// prompt. En vez de un textarea monolítico de 4000 caracteres, el administrador
// agrega tarjetas independientes (título + contenido + icono) que se compilan
// automáticamente al guardar.
//
// Cada tarjeta es un tema que la IA debe conocer: horarios, cobertura, estados
// de envío, políticas de retiro, etc. El diseño sigue el patrón de la Base de
// conocimiento del MU mockup (/m/soporte): grid responsive de tarjetas con
// icono, título y preview del contenido.
//
// Persistencia: el array de entradas se serializa como JSON dentro del campo
// system_prompt (marcadores ---KNOWLEDGE_BASE--- / ---END_KNOWLEDGE_BASE---) y
// también viaja como `knowledge_base` en el payload del PUT. El backend puede
// aceptar o ignorar este campo extra; en cualquier caso, el system_prompt
// compilado siempre es válido para la IA.

const TITLE_MAX = 120;
const CONTENT_MAX = 2000;
const ICON_MAX = 10;

// Emojis sugeridos para que el administrador no tenga que abrir el teclado
// de emojis del sistema. Agrupados por categoría semántica para el sector.
const EMOJI_SUGGESTIONS = [
  // Operación
  { emoji: "📦", label: "Paquete" },
  { emoji: "🚚", label: "Camión" },
  { emoji: "📍", label: "Ubicación" },
  { emoji: "🗺️", label: "Mapa" },
  { emoji: "🏭", label: "Bodega" },
  // Tiempo
  { emoji: "🕐", label: "Horario" },
  { emoji: "📅", label: "Calendario" },
  { emoji: "⏰", label: "Alarma" },
  // Documentos
  { emoji: "📋", label: "Documento" },
  { emoji: "💳", label: "Pago" },
  { emoji: "🧾", label: "Factura" },
  { emoji: "📝", label: "Formulario" },
  // Comunicación
  { emoji: "📞", label: "Teléfono" },
  { emoji: "📧", label: "Email" },
  { emoji: "💬", label: "Chat" },
  // Estados
  { emoji: "✅", label: "Confirmado" },
  { emoji: "❌", label: "Cancelado" },
  { emoji: "⚠️", label: "Advertencia" },
  { emoji: "ℹ️", label: "Información" },
  // Personas
  { emoji: "👤", label: "Persona" },
  { emoji: "👥", label: "Grupo" },
  { emoji: "🏢", label: "Empresa" },
  { emoji: "🔒", label: "Seguridad" },
  { emoji: "⭐", label: "Destacado" },
];

const EMPTY_DRAFT = { title: "", content: "", icon: "📋" };

// ─── Ids locales de las entradas ────────────────────────────────────────────
// El editor indexa todo por id (clave de React, edición, borrado), pero el
// backend puede devolver entradas sin él (base de conocimiento vieja o armada a
// mano): sin id, "Editar" no encontraba la entrada y el guardado terminaba
// AGREGANDO un duplicado. A las entradas sin id se les asigna uno local al
// cargar (`kb-<índice>-<random>`).
//
// Ese id es de la UI, no del contrato: el array de entradas ES el payload (viaja
// dentro de prompt.knowledge_base), así que se quita antes de cada onChange. Los
// ids que sí vinieron del backend —y el de los artículos creados acá, que ya se
// comportaba así antes— viajan intactos.
/**
 * @param {Array} list
 * @param {Set<string>} generated - ids que inventó el editor.
 * @returns {Array} la lista sin esos ids (misma forma que mandó el backend).
 */
export function stripLocalIds(list, generated) {
  return (list || []).filter(Boolean).map((entry) => {
    if (!generated || !generated.has(entry?.id)) return entry;
    const rest = { ...entry };
    delete rest.id;
    return rest;
  });
}

// ─── Vista previa del contenido (2 líneas, truncado) ──────────────────────
function ContentPreview({ content }) {
  const text = (content || "").trim();
  if (!text) return null;
  return (
    <p className="text-xs text-fg-muted line-clamp-2 mt-1 leading-relaxed">
      {text}
    </p>
  );
}

// ─── Tarjeta individual ──────────────────────────────────────────────────
function KnowledgeCard({ entry, onEdit, onDelete, disabled }) {
  const icon = (entry.icon || "📋").trim().slice(0, ICON_MAX) || "📋";

  return (
    <Card
      padding="md"
      className={cn(
        "group relative flex flex-col",
        "transition-shadow duration-150 ease-out motion-reduce:transition-none",
        "hover:shadow-soft",
        disabled && "opacity-60 pointer-events-none",
      )}
    >
      {/* Cabecera: icono + título */}
      <div className="flex items-start gap-3 min-w-0">
        <span
          className="shrink-0 text-2xl leading-none select-none"
          aria-hidden="true"
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <CardTitle className="text-sm leading-snug break-words">
            {entry.title}
          </CardTitle>
        </div>
      </div>

      {/* Preview del contenido */}
      <div className="mt-2 mb-1 min-w-0">
        <ContentPreview content={entry.content} />
      </div>

      {/* Acciones — visibles al hover en desktop, siempre en mobile */}
      <CardFooter className="mt-auto pt-3 gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="px-2 text-xs"
          onClick={() => onEdit(entry)}
          disabled={disabled}
          iconLeft={<Pencil className="w-3.5 h-3.5" aria-hidden="true" />}
        >
          Editar
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="px-2 text-xs text-status-error hover:bg-status-error-soft"
          onClick={() => onDelete(entry)}
          disabled={disabled}
          iconLeft={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
        >
          Eliminar
        </Button>
      </CardFooter>
    </Card>
  );
}

// ─── Modal de agregar / editar ────────────────────────────────────────────
function KnowledgeFormModal({ open, mode, draft, onChange, onSubmit, onClose, saving }) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState({});

  const errors = useMemo(() => {
    const errs = {};
    if (!(draft.title || "").trim()) errs.title = t("El título es obligatorio.");
    else if ((draft.title || "").length > TITLE_MAX) errs.title = t("El título es demasiado largo.");
    if (!(draft.content || "").trim()) errs.content = t("El contenido es obligatorio.");
    else if ((draft.content || "").length > CONTENT_MAX) errs.content = t("El contenido supera el máximo permitido.");
    return errs;
  }, [draft, t]);

  const hasErrors = Object.keys(errors).length > 0;

  // Cada apertura arranca sin errores visibles.
  React.useEffect(() => {
    if (open) setTouched({});
  }, [open]);

  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));
  const errorFor = (field) => (touched[field] ? errors[field] : undefined);

  const handleSubmit = () => {
    setTouched({ title: true, content: true });
    if (hasErrors) return;
    onSubmit();
  };

  const contentLen = (draft.content || "").length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={mode === "edit" ? t("Editar artículo") : t("Nuevo artículo")}
      description={t(
        "Cada artículo es un tema que la IA conocerá al responder: horarios, coberturas, políticas, estados de envío…",
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button className="hd-brand-solid" onClick={handleSubmit} loading={saving}>
            {mode === "edit" ? t("Guardar cambios") : t("Agregar artículo")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Título */}
        <div className="space-y-1.5">
          <FieldLabel className="hd-field-label" htmlFor="kb-title">
            {t("Título")}
          </FieldLabel>
          <Input
            id="kb-title"
            value={draft.title}
            maxLength={TITLE_MAX}
            invalid={!!errorFor("title")}
            onChange={(e) => onChange({ ...draft, title: e.target.value })}
            onBlur={() => markTouched("title")}
            placeholder={t("Ej: Horarios de atención")}
            aria-describedby={errorFor("title") ? "kb-title-error" : undefined}
          />
          {errorFor("title") ? (
            <p id="kb-title-error" className="text-xs text-status-error">
              {errorFor("title")}
            </p>
          ) : (
            <p className="text-xs text-fg-muted">
              {t("Un título claro para que sepas qué cubre este artículo.")}
            </p>
          )}
        </div>

        {/* Contenido */}
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <FieldLabel className="hd-field-label" htmlFor="kb-content">
              {t("Contenido")}
            </FieldLabel>
            <span
              className={cn(
                "text-xs tabular-nums",
                contentLen > CONTENT_MAX ? "text-status-error font-semibold" : "text-fg-subtle",
              )}
            >
              {contentLen} / {CONTENT_MAX}
            </span>
          </div>
          <textarea
            id="kb-content"
            rows={5}
            value={draft.content}
            onChange={(e) => onChange({ ...draft, content: e.target.value.slice(0, CONTENT_MAX) })}
            onBlur={() => markTouched("content")}
            placeholder={t(
              "Escribe lo que la IA debe saber sobre este tema. Sé concreto: datos, horarios, nombres, políticas…",
            )}
            aria-describedby={errorFor("content") ? "kb-content-error" : undefined}
            className={cn(
              "w-full rounded-lg border bg-bg px-3 py-2 text-sm text-fg resize-y",
              "placeholder:text-fg-subtle",
              "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
              errorFor("content") ? "border-status-error" : "border-border",
            )}
          />
          {errorFor("content") ? (
            <p id="kb-content-error" className="text-xs text-status-error">
              {errorFor("content")}
            </p>
          ) : (
            <p className="text-xs text-fg-muted">
              {t("Este texto se incluirá en el system prompt para que la IA lo use al responder.")}
            </p>
          )}
        </div>

        {/* Icono (selector de emoji) */}
        <div className="space-y-1.5">
          <FieldLabel className="hd-field-label">
            {t("Icono")}
          </FieldLabel>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl leading-none select-none" aria-hidden="true">
              {(draft.icon || "📋").trim().slice(0, ICON_MAX) || "📋"}
            </span>
            <Input
              id="kb-icon"
              value={draft.icon}
              maxLength={ICON_MAX}
              onChange={(e) => onChange({ ...draft, icon: e.target.value })}
              placeholder="📋"
              className="w-24 font-mono text-center text-lg"
              aria-label={t("Icono (emoji)")}
            />
            <p className="text-xs text-fg-muted">
              {t("Un emoji para identificar el artículo.")}
            </p>
          </div>
          <div
            role="group"
            aria-label={t("Emojis sugeridos")}
            className="flex flex-wrap gap-1"
          >
            {EMOJI_SUGGESTIONS.map(({ emoji, label }) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onChange({ ...draft, icon: emoji })}
                aria-label={label}
                title={label}
                className={cn(
                  "inline-flex items-center justify-center w-9 h-9 rounded-md text-lg",
                  "hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  "transition-colors duration-100",
                  draft.icon === emoji && "bg-brand-50 ring-1 ring-brand-300",
                )}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Vista previa del prompt compilado ───────────────────────────────────
function CompiledPreview({ instructions, entries, defaultInstructions }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const compiled = useMemo(() => {
    const parts = [];
    const header = (instructions || "").trim();
    if (header) parts.push(header);

    const kbEntries = (entries || []).filter(
      (e) => (e.title || "").trim() && (e.content || "").trim(),
    );

    if (kbEntries.length > 0) {
      parts.push("");
      parts.push("## Base de conocimiento");
      kbEntries.forEach((entry) => {
        parts.push(`### ${entry.title.trim()}`);
        parts.push(entry.content.trim());
        parts.push("");
      });
    }

    // Si no hay nada, mostrar el default para que no esté vacío
    if (parts.length === 0 && defaultInstructions) {
      return defaultInstructions;
    }

    return parts.join("\n").trim();
  }, [instructions, entries, defaultInstructions]);

  const lineCount = compiled.split("\n").length;
  const charCount = compiled.length;

  return (
    <div className="rounded-lg border border-border bg-bg-subtle overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-bg-muted/50 transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-fg-muted uppercase tracking-wider">
          <Eye className="w-3.5 h-3.5" aria-hidden="true" />
          {t("Vista previa del system prompt")}
        </span>
        <span className="flex items-center gap-2 text-[11px] tabular-nums text-fg-muted">
          {t("{{chars}} caracteres · {{lines}} líneas", {
            chars: charCount,
            lines: lineCount,
          })}
          {open ? (
            <ChevronUp className="w-4 h-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="w-4 h-4" aria-hidden="true" />
          )}
        </span>
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3 animate-fade-in motion-reduce:animate-none">
          <pre className="text-xs text-fg-muted whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto">
            {compiled || (
              <span className="text-fg-subtle italic">
                {t("El prompt está vacío. Agrega instrucciones o artículos a la base de conocimiento.")}
              </span>
            )}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── Vista principal ──────────────────────────────────────────────────────
export default function KnowledgeBaseEditor({
  entries,
  instructions,
  onChange,
  onInstructionsChange,
  disabled = false,
}) {
  const { t } = useTranslation();

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Sufijo aleatorio por MONTAJE. El id se deriva del ÍNDICE en cada render, no
  // de un caché por identidad de objeto: al emitir, las entradas sin id salen
  // como objetos nuevos (el id se quita), así que un caché por identidad las
  // vería como entradas distintas y les daría un id nuevo en cada guardado —
  // dejando obsoleto el `editingId` de un modal abierto. Con el índice, el id es
  // estable mientras el array conserve el orden (validación y borrado usan el
  // array VIGENTE, así que el modal del borrado nunca queda desfasado).
  const idSuffixRef = useRef(Math.random().toString(36).slice(2, 8));
  // Ids que inventó este editor: son los ÚNICOS que se quitan del payload.
  // El `add` es idempotente, así que el doble render de StrictMode no lo altera.
  const generatedIdsRef = useRef(new Set());

  const normalizedEntries = useMemo(() => {
    const suffix = idSuffixRef.current;
    const generated = generatedIdsRef.current;
    return (entries || []).map((entry, index) => {
      if (!entry || entry.id) return entry;
      const id = `kb-${index}-${suffix}`;
      generated.add(id);
      return { ...entry, id };
    });
  }, [entries]);

  // Único camino hacia el padre: quita los ids locales para no cambiar el
  // payload que se envía al backend.
  const emit = useCallback(
    (next) => onChange(stripLocalIds(next, generatedIdsRef.current)),
    [onChange],
  );

  // ── Handlers ──
  const openCreate = useCallback(() => {
    setFormMode("create");
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT });
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((entry) => {
    setFormMode("edit");
    setEditingId(entry.id);
    setDraft({
      title: entry.title || "",
      content: entry.content || "",
      icon: entry.icon || "📋",
    });
    setFormOpen(true);
  }, []);

  const handleSubmit = useCallback(() => {
    // Sobre las entradas NORMALIZADAS: una entrada sin id del backend ya tiene
    // su id local, así que la edición encuentra la fila en vez de duplicarla.
    const next = [...normalizedEntries];
    const payload = {
      title: (draft.title || "").trim(),
      content: (draft.content || "").trim(),
      icon: (draft.icon || "📋").trim().slice(0, ICON_MAX) || "📋",
    };

    if (formMode === "edit" && editingId) {
      const idx = next.findIndex((e) => e.id === editingId);
      if (idx !== -1) next[idx] = { ...next[idx], ...payload };
    } else {
      next.push({ id: `kb-${Date.now()}`, ...payload });
    }

    emit(next);
    setFormOpen(false);
  }, [draft, formMode, editingId, normalizedEntries, emit]);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    emit(normalizedEntries.filter((e) => e.id !== deleteTarget.id));
    setDeleteTarget(null);
  }, [deleteTarget, normalizedEntries, emit]);

  const hasEntries = normalizedEntries.length > 0;

  return (
    <div className="space-y-4">
      {/* Instrucciones generales (texto libre breve, arriba de los artículos) */}
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <FieldLabel className="hd-field-label" htmlFor="kb-instructions">
            {t("Instrucciones generales")}
          </FieldLabel>
          <span className="text-[11px] text-fg-muted">
            {t("Opcional — define el tono, rol y reglas base del asistente.")}
          </span>
        </div>
        <textarea
          id="kb-instructions"
          value={instructions || ""}
          onChange={(e) => onInstructionsChange?.(e.target.value)}
          disabled={disabled}
          rows={4}
          placeholder={t(
            "Ej.: Eres el asistente virtual de [empresa], courier de última milla en Chile. Responde en español, breve y cordial. Si no sabes algo, deriva a un agente humano.",
          )}
          className={cn(
            "w-full rounded-md border border-border bg-bg !p-3 !text-base md:!text-sm text-fg resize-y",
            "placeholder:text-fg-subtle",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        />
      </div>

      {/* Cabecera de la sección */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="w-4 h-4 text-brand-600 shrink-0" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-fg">
            {t("Base de conocimiento")}
          </h3>
          {hasEntries && (
            <span className="text-xs text-fg-muted tabular-nums shrink-0">
              {normalizedEntries.length}
            </span>
          )}
        </div>
        <Button
          size="sm"
          className="hd-brand-solid shrink-0"
          iconLeft={<Plus className="w-3.5 h-3.5" aria-hidden="true" />}
          onClick={openCreate}
          disabled={disabled}
        >
          {t("Agregar")}
        </Button>
      </div>
      <p className="text-xs text-fg-muted -mt-3">
        {t(
          "Cada artículo le enseña a la IA un tema específico de tu operación. Agrega los que necesites: mientras más preciso, mejores respuestas.",
        )}
      </p>

      {/* Grid de tarjetas — mismo patrón responsive que el MU */}
      {hasEntries ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {normalizedEntries.map((entry) => (
            <KnowledgeCard
              key={entry.id}
              entry={entry}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              disabled={disabled}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-bg">
          <EmptyState
            icon={<BookOpen aria-hidden="true" />}
            title={t("Sin artículos en la base de conocimiento")}
            description={t(
              "Agrega temas como horarios de atención, zonas de cobertura, políticas de retiro o estados de envío. La IA usará esta información para responder con precisión.",
            )}
            action={
              <Button
                className="hd-brand-solid"
                iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                onClick={openCreate}
                disabled={disabled}
              >
                {t("Agregar primer artículo")}
              </Button>
            }
          />
        </div>
      )}

      {/* Vista previa del prompt compilado */}
      <CompiledPreview
        instructions={instructions}
        entries={entries}
      />

      {/* Modal de agregar / editar */}
      <KnowledgeFormModal
        open={formOpen}
        mode={formMode}
        draft={draft}
        onChange={setDraft}
        onSubmit={handleSubmit}
        onClose={() => setFormOpen(false)}
        saving={false}
      />

      {/* Modal de confirmación de eliminado */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        size="sm"
        title={t("Eliminar artículo")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              {t("Eliminar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">
          {t("Vas a eliminar este artículo de la base de conocimiento:")}
        </p>
        <p className="mt-1 text-sm font-semibold text-fg break-words">
          {deleteTarget?.title}
        </p>
        <p className="text-sm text-fg-muted mt-3">
          {t(
            "La IA dejará de recibir esta información al responder. Esta acción no se puede deshacer.",
          )}
        </p>
      </Modal>
    </div>
  );
}
